import React, { useState, useEffect, useCallback } from 'react';
import { BookmarkIcon } from './Icons';
import { bookmarksAPI } from '../services/api';
import styles from '../styles/ContentCard.module.css';

/**
 * Article Card Component
 * Displays educational article cards
 *
 * Props:
 * - id: Article ID (for bookmarking)
 * - title: Article title
 * - slug: Article slug for linking
 * - category: Article category (optional, not in Strapi schema)
 * - excerpt: Optional excerpt/summary
 * - heroImage: Optional hero image URL
 * - onClick: Optional click handler (receives slug)
 * - showBookmark: Show bookmark button (default: true)
 * - isBookmarked: Initial bookmark state (optional, will check API if not provided)
 */
function ArticleCard({ id, title, slug, category, excerpt, heroImage, onClick, showBookmark = true, isBookmarked: initialIsBookmarked }) {
  const [isBookmarked, setIsBookmarked] = useState(initialIsBookmarked || false);
  const [isTogglingBookmark, setIsTogglingBookmark] = useState(false);
  const categoryLabel = category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Article';

  const checkBookmarkStatus = useCallback(async () => {
    try {
      const result = await bookmarksAPI.isBookmarked('article', id);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[ArticleCard] Failed to check bookmark status:', error);
    }
  }, [id]);

  // Check if bookmarked on mount (only if not provided as prop)
  useEffect(() => {
    if (showBookmark && id && initialIsBookmarked === undefined) {
      checkBookmarkStatus();
    }
  }, [id, showBookmark, initialIsBookmarked, checkBookmarkStatus]);

  // Update local state when prop changes
  useEffect(() => {
    if (initialIsBookmarked !== undefined) {
      setIsBookmarked(initialIsBookmarked);
    }
  }, [initialIsBookmarked]);

  const handleBookmarkClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    if (isTogglingBookmark) return;

    setIsTogglingBookmark(true);
    try {
      const result = await bookmarksAPI.toggleBookmark('article', id);
      setIsBookmarked(result.bookmarked);
    } catch (error) {
      console.error('[ArticleCard] Failed to toggle bookmark:', error);
    } finally {
      setIsTogglingBookmark(false);
    }
  };

  return (
    <div
      className={styles.contentCard}
      onClick={() => onClick?.(slug, { title, category })}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {heroImage && (
        <div className={styles.contentCardImage}>
          <img src={heroImage} alt={title} className={styles.image} />
          <div className={styles.contentCardBadge}>{categoryLabel}</div>
          {showBookmark && id && (
            <button
              className={styles.bookmarkButton}
              onClick={handleBookmarkClick}
              disabled={isTogglingBookmark}
              aria-label={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
            >
              <BookmarkIcon size={20} filled={isBookmarked} />
            </button>
          )}
        </div>
      )}

      <div className={styles.contentCardContent}>
        {!heroImage && <div className={styles.contentCardBadge}>{categoryLabel}</div>}
        <div className={styles.contentCardTitle}>{title}</div>

        {excerpt && (
          <div className={styles.contentCardDescription}>
            {excerpt}
          </div>
        )}

        <div className={styles.contentCardMeta}>
          <div className={styles.contentCardMetaItem}>
            Read Article
          </div>
        </div>
      </div>
    </div>
  );
}

export default ArticleCard;
